import React, { useEffect, useRef } from "react";

const GlobalParallaxBackground = () => {
  const rootRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    // Respect reduced motion
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      el.style.setProperty("--gpb-y", "0px");
      return;
    }

    let rafId = 0;

    const update = () => {
      const scrollY = window.scrollY || window.pageYOffset || 0;

      // Vastasuuntaan scrolliin nähden (negatiivinen kerroin)
      // Säädä tätä: -0.06 ... -0.12
      let y = scrollY * -0.08;

      // Clamp ettei karkaa liian voimakkaaksi pitkillä sivuilla
      if (y < -80) y = -80;
      if (y > 80) y = 80;

      el.style.setProperty("--gpb-y", `${y}px`);
      rafId = 0;
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(update);
    };

    const onResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    // initial
    update();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="pointer-events-none fixed left-0 right-0 top-[-25vh] bottom-[-25vh] z-0 overflow-hidden"
      style={{ "--gpb-y": "0px" }}
    >
      {/* Pohjakerros: hienovarainen sävymassa */}
      <div className="absolute inset-0 bg-white dark:bg-blue-950" />

      {/* Parallax-kerros */}
      <div
        className="absolute inset-[-10%] will-change-transform"
        style={{
          transform: "translate3d(0, var(--gpb-y), 0) scale(1.06)",
          opacity: 0.55,
          backgroundImage: `
            radial-gradient(circle at 14% 18%, rgba(255,255,255,0.08), transparent 32%),
            radial-gradient(circle at 84% 22%, rgba(56,189,248,0.08), transparent 28%),
            radial-gradient(circle at 74% 70%, rgba(163,230,53,0.07), transparent 24%),
            radial-gradient(circle at 28% 80%, rgba(255,255,255,0.05), transparent 30%),

            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
          `,
          backgroundSize: `
            auto,
            auto,
            auto,
            auto,
            30px 30px,
            30px 30px
          `,
          backgroundPosition: `
            center,
            center,
            center,
            center,
            center,
            center
          `,
          filter: "blur(0.3px) saturate(0.95)",
        }}
      />

      {/* Lisäkerros: pehmeä diagonaalinen hohto (myös parallax) */}
      <div
        className="absolute inset-[-6%] will-change-transform"
        style={{
          transform: "translate3d(0, calc(var(--gpb-y) * 0.55), 0) scale(1.03)",
          opacity: 0.28,
          background:
            "linear-gradient(120deg, transparent 15%, rgba(255,255,255,0.06) 40%, transparent 85%)",
          filter: "blur(10px)",
        }}
      />

      {/* Vignette / luettavuus */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(circle at 50% 35%, transparent 0%, rgba(10,15,40,0.08) 65%, rgba(10,15,40,0.14) 100%)
          `,
        }}
      />

      {/* Kevyt "film grain" -fiilis ilman ulkoista kuvaa */}
      <div
        className="absolute inset-0 opacity-[0.045] mix-blend-soft-light"
        style={{
          backgroundImage: `
            radial-gradient(rgba(255,255,255,0.9) 0.6px, transparent 0.8px),
            radial-gradient(rgba(255,255,255,0.6) 0.5px, transparent 0.7px)
          `,
          backgroundSize: "4px 4px, 6px 6px",
          backgroundPosition: "0 0, 2px 2px",
        }}
      />
    </div>
  );
};

export default GlobalParallaxBackground;

